/* eslint-disable react/no-unescaped-entities, react-hooks/set-state-in-effect -- texte FR + fetch-on-mount, fonctionnel et validé */
import React, { useState, useEffect, useCallback } from 'react';
import {
    BarChart3, Users, Building2, Target, Search, ChevronRight,
    RefreshCw, X, AlertCircle, Zap, Activity, Shield, Gauge, FileDown
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { STATUS_CONFIG } from '../../components/internal/constants';

// Tab components
import MonitoringTab from '../../components/monitoring/MonitoringTab';
import ActivationTab from '../../components/internal/ActivationTab';
import UsageTab from '../../components/internal/UsageTab';
import RetentionTab from '../../components/internal/RetentionTab';
import AnalyticsTab from '../../components/internal/AnalyticsTab';
import AgenciesTab from '../../components/internal/AgenciesTab';
import RequestDetailModal from '../../components/internal/RequestDetailModal';
import CreateAgencyModal from '../../components/internal/CreateAgencyModal';

export default function InternalDashboard() {
    const { logout } = useAuth();
    const [dashboard, setDashboard] = useState(null);
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hasAccess, setHasAccess] = useState(null);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [createAgencyRequest, setCreateAgencyRequest] = useState(null);
    const [statusFilter, setStatusFilter] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [period, setPeriod] = useState('month');
    const [activeTab, setActiveTab] = useState('prospects');

    const checkAccess = useCallback(async () => {
        try { const res = await api.get('/internal/check-access'); setHasAccess(res.data.has_access); }
        catch { setHasAccess(false); }
    }, []);

    const fetchData = useCallback(async () => {
        try {
            const [dashRes, reqRes] = await Promise.all([
                api.get(`/internal/dashboard?period=${period}`),
                api.get('/internal/demo-requests')
            ]);
            setDashboard(dashRes.data);
            setRequests(reqRes.data.requests || []);
        } catch { /* noop */ } finally { setLoading(false); }
    }, [period]);

    useEffect(() => { checkAccess(); }, [checkAccess]);
    useEffect(() => { if (hasAccess) fetchData(); }, [hasAccess, fetchData]);

    const downloadPlaquette = async () => {
        try {
            const res = await api.get('/super-admin/plaquette', { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            if (window.self !== window.top) {
                // Aperçu intégré (iframe) : on ouvre dans un nouvel onglet de premier niveau
                window.open(url, '_blank', 'noopener');
                toast.success('Plaquette ouverte dans un nouvel onglet');
            } else {
                const a = document.createElement('a');
                a.href = url;
                a.download = 'Homly-Plaquette-Commerciale.pdf';
                document.body.appendChild(a);
                a.click();
                a.remove();
                toast.success('Plaquette téléchargée');
            }
            setTimeout(() => window.URL.revokeObjectURL(url), 4000);
        } catch { toast.error('Erreur lors du téléchargement de la plaquette'); }
    };

    if (hasAccess === false) return (
        <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950/20 to-slate-950 flex items-center justify-center p-8">
            <Card className="bg-slate-900/50 border-white/10 max-w-md">
                <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4"><X className="w-8 h-8 text-red-400" /></div>
                    <h2 className="text-xl font-bold text-white mb-2">Accès refusé</h2>
                    <p className="text-white/60">Ce dashboard est réservé à l'équipe Homly.</p>
                </CardContent>
            </Card>
        </div>
    );

    if (loading || hasAccess === null) return (
        <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950/20 to-slate-950 flex items-center justify-center">
            <RefreshCw className="w-8 h-8 text-purple-400 animate-spin" />
        </div>
    );

    const filteredRequests = requests.filter(r => {
        if (statusFilter && r.status !== statusFilter) return false;
        if (searchQuery) {
            const s = searchQuery.toLowerCase();
            return r.company_name?.toLowerCase().includes(s) || r.contact_name?.toLowerCase().includes(s) || r.email?.toLowerCase().includes(s);
        }
        return true;
    });

    const stats = dashboard?.demo_requests?.status_breakdown || {};

    const TABS = [
        { key: 'prospects', icon: Target, label: 'Acquisition', color: 'bg-purple-500' },
        { key: 'activation', icon: Zap, label: 'Activation', color: 'bg-green-500' },
        { key: 'usage', icon: Activity, label: 'Usage Produit', color: 'bg-blue-500' },
        { key: 'retention', icon: Shield, label: 'Rétention & Santé', color: 'bg-orange-500' },
        { key: 'agencies', icon: Building2, label: 'Agences', color: 'bg-purple-500' },
        { key: 'analytics', icon: BarChart3, label: 'Analytics', color: 'bg-purple-500' },
        { key: 'monitoring', icon: Gauge, label: 'Monitoring', color: 'bg-emerald-500' },
    ];

    const KPI_CARDS = [
        [
            { label: 'Demandes de démo', value: dashboard?.demo_requests?.total || 0, sub: 'sur la période', color: 'text-white' },
            { label: 'Taux de conversion', value: `${dashboard?.demo_requests?.conversion_rate || 0}%`, sub: 'demandes → agences', color: 'text-green-400' },
            { label: 'Agences actives', value: dashboard?.agencies?.active || 0, sub: `/ ${dashboard?.agencies?.total || 0} totales`, color: 'text-purple-400' },
            { label: 'Utilisateurs', value: dashboard?.users?.total || 0, sub: `${dashboard?.users?.agents || 0} agents · ${dashboard?.users?.sellers || 0} vendeurs`, color: 'text-blue-400' },
        ],
        [
            { label: 'MRR estimé', value: `${((dashboard?.agencies?.active || 0) * 94).toLocaleString()}€`, sub: '~94€/agence/mois', color: 'text-[#FF914D]' },
            { label: 'Mandats créés', value: dashboard?.mandates?.total || 0, sub: `${dashboard?.mandates?.active || 0} actifs`, color: 'text-white' },
            { label: 'Nouveaux prospects', value: stats.new || 0, sub: 'à contacter', color: 'text-cyan-400' },
            { label: 'Pipeline', value: (stats.contacted || 0) + (stats.demo_scheduled || 0), sub: `${stats.contacted || 0} contactés · ${stats.demo_scheduled || 0} démos`, color: 'text-yellow-400' },
        ]
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950/20 to-slate-950">
            {/* Header */}
            <div className="border-b border-white/10 bg-slate-900/50 backdrop-blur-xl sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div><h1 className="text-2xl font-bold text-white">Homly Entreprise</h1><p className="text-white/60 text-sm">Dashboard interne - CRM</p></div>
                        <div className="flex items-center gap-3 flex-wrap">
                            <select value={period} onChange={(e) => setPeriod(e.target.value)} className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm">
                                <option value="week">7 jours</option><option value="month">30 jours</option><option value="quarter">90 jours</option><option value="year">1 an</option>
                            </select>
                            <Button onClick={downloadPlaquette} variant="outline" size="sm" className="border-[#FF914D]/40 text-[#FF914D] hover:bg-[#FF914D]/10" data-testid="internal-download-plaquette-btn"><FileDown size={14} className="mr-2" />Plaquette</Button>
                            <Button onClick={fetchData} variant="outline" size="sm" className="border-white/20 text-white"><RefreshCw size={14} className="mr-2" />Actualiser</Button>
                            <Button onClick={async () => { await logout(); window.location.href = '/login'; }} variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="crm-logout-btn">
                                <X size={14} className="mr-1" />Déconnexion
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
                {/* KPI Cards */}
                {KPI_CARDS.map((row, rowIdx) => (
                    <div key={rowIdx} className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        {row.map((kpi, i) => (
                            <Card key={i} className="bg-slate-900/50 border-white/10">
                                <CardContent className="p-4">
                                    <p className="text-white/50 text-xs mb-1">{kpi.label}</p>
                                    <p className={`text-3xl font-bold ${kpi.color}`}>{kpi.value}</p>
                                    <p className="text-xs text-white/40 mt-1">{kpi.sub}</p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ))}

                {/* Tabs */}
                <div className="flex gap-2 border-b border-white/10 pb-2 overflow-x-auto">
                    {TABS.map(tab => (
                        <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                            className={`px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${activeTab === tab.key ? `${tab.color} text-white` : 'text-white/60 hover:bg-white/5'}`}
                            data-testid={tab.key === 'monitoring' ? 'monitoring-tab-btn' : undefined}>
                            <tab.icon className="w-4 h-4 inline mr-2" />{tab.label}
                        </button>
                    ))}
                </div>

                {/* Tab Content */}
                {activeTab === 'monitoring' && <MonitoringTab />}
                {activeTab === 'activation' && <ActivationTab />}
                {activeTab === 'usage' && <UsageTab />}
                {activeTab === 'retention' && <RetentionTab />}
                {activeTab === 'analytics' && <AnalyticsTab period={period} />}
                {activeTab === 'agencies' && (
                    <Card className="bg-slate-900/50 border-white/10">
                        <CardHeader><CardTitle className="text-white">Agences</CardTitle></CardHeader>
                        <CardContent><AgenciesTab /></CardContent>
                    </Card>
                )}
                {activeTab === 'prospects' && (
                    <>
                        {/* Pipeline Stats */}
                        <Card className="bg-slate-900/50 border-white/10">
                            <CardHeader className="pb-2"><CardTitle className="text-white text-lg">Pipeline</CardTitle></CardHeader>
                            <CardContent>
                                <div className="flex flex-wrap gap-2 sm:gap-4">
                                    {Object.entries(STATUS_CONFIG).map(([key, config]) => {
                                        const count = stats[key] || 0;
                                        const StatusIcon = config.icon;
                                        return (
                                            <button key={key} onClick={() => setStatusFilter(statusFilter === key ? '' : key)}
                                                className={`flex items-center gap-2 px-3 sm:px-4 py-2 sm:py-3 rounded-xl transition-all ${statusFilter === key ? config.color + ' text-white' : 'bg-white/5 hover:bg-white/10'}`}>
                                                <StatusIcon size={16} className={statusFilter === key ? 'text-white' : config.textColor} />
                                                <span className={`font-medium text-sm sm:text-base ${statusFilter === key ? 'text-white' : 'text-white/80'}`}>{count}</span>
                                                <span className={`text-xs sm:text-sm hidden sm:inline ${statusFilter === key ? 'text-white/80' : 'text-white/50'}`}>{config.label}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Requests List */}
                        <Card className="bg-slate-900/50 border-white/10">
                            <CardHeader>
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                    <CardTitle className="text-white">Demandes de démo</CardTitle>
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                                        <input type="text" placeholder="Rechercher..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                                            className="pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm w-full sm:w-64 placeholder-white/40" />
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                                    {filteredRequests.length === 0 ? (
                                        <div className="text-center py-12">
                                            <Target className="w-12 h-12 text-white/20 mx-auto mb-4" />
                                            <p className="text-white/60">Aucune demande</p>
                                            <p className="text-white/40 text-sm">Les demandes de démo apparaîtront ici</p>
                                        </div>
                                    ) : filteredRequests.map((request) => {
                                        const StatusIcon = STATUS_CONFIG[request.status]?.icon || AlertCircle;
                                        return (
                                            <div key={request.id} onClick={() => setSelectedRequest(request)}
                                                className="p-4 bg-white/5 hover:bg-white/10 rounded-xl cursor-pointer transition-colors flex items-center justify-between gap-4">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-3 mb-1">
                                                        <h4 className="text-white font-medium truncate">{request.company_name}</h4>
                                                        <span className={`px-2 py-0.5 rounded-full text-xs flex items-center gap-1 ${STATUS_CONFIG[request.status]?.color} text-white`}>
                                                            <StatusIcon size={10} /><span className="hidden sm:inline">{STATUS_CONFIG[request.status]?.label}</span>
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-4 text-sm text-white/50">
                                                        <span>{request.contact_name}</span><span className="hidden sm:inline">•</span><span className="hidden sm:inline">{request.email}</span>
                                                    </div>
                                                </div>
                                                <div className="text-right flex-shrink-0">
                                                    <p className="text-white/50 text-xs">{new Date(request.created_at).toLocaleDateString('fr-FR')}</p>
                                                    <ChevronRight className="w-4 h-4 text-white/30 ml-auto mt-1" />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </CardContent>
                        </Card>
                    </>
                )}
            </div>

            {/* Modals */}
            <RequestDetailModal isOpen={!!selectedRequest} onClose={() => setSelectedRequest(null)} request={selectedRequest}
                onUpdate={() => { fetchData(); setSelectedRequest(null); }}
                onCreateAgency={(req) => { setSelectedRequest(null); setCreateAgencyRequest(req); }} />
            <CreateAgencyModal isOpen={!!createAgencyRequest} onClose={() => setCreateAgencyRequest(null)} request={createAgencyRequest}
                onCreated={() => { fetchData(); setCreateAgencyRequest(null); }} />
        </div>
    );
}
